import {
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatOptionSelectionChange } from '@angular/material/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { first, map, startWith, tap } from 'rxjs/operators';
import { GalaxyCoreInputDirective } from '../core/core-input.directive';
import { SelectInputOption, SelectInputOptionInterface } from '../input.interface';

/** Deprecated - can be updated to use Galaxy Form Field */
@Component({
  selector: 'glxy-search-select-input',
  templateUrl: './search-select-input.component.html',
  styleUrls: ['./../core/core.material-override.scss', './search-select-input.component.scss'],
})
export class SearchSelectInputComponent
  extends GalaxyCoreInputDirective<string>
  implements OnInit, OnDestroy, OnChanges
{
  @HostBinding('class') class = 'glxy-search-select-input';

  /** The optional label to give to the input */
  @Input() label = '';

  /** The optional placeholder text string to display when the input is empty and not focused on */
  @Input() placeholder = '';

  /** The list of values that you want to display in the select dropdown */
  @Input() options: SelectInputOption[] = [];

  /** Optional value to overwrite what value is selected on the input */
  @Input() set value(val: string) {
    const option = this.options.find((opt: SelectInputOption) => {
      const optt = opt as SelectInputOptionInterface;
      if (optt.value && typeof optt.value === 'string') {
        return optt.value.toString().toLowerCase() === val.toLowerCase();
      }
      return undefined;
    });
    if (option) {
      this.formControl?.setValue(option);
    }
  }

  /** Text to display to give a hint what the purpose of the search select input is */
  @Input() hint?: string;

  /** To toggle if you only want to be able to select one value or multiple from the dropdown */
  @Input() selection: 'single' | 'multiple' = 'single';

  /** Emits when the selected value(s) in the input has been changed or updated. */
  @Output() selectedOptions = new EventEmitter<SelectInputOption[] | SelectInputOption>();

  subscriptions: Subscription[] = [];
  selectedValue?: SelectInputOption;
  selectedValues: SelectInputOption[] = [];
  filteredOptions$$ = new BehaviorSubject<SelectInputOption[]>([]);

  isActive = false;

  validatorError$?: Observable<string>;
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith({ label: '' }),
        map((value: SelectInputOption | string) => {
          if (typeof value === 'string') {
            return this._filter(value);
          }
          return this._filter(value.label);
        }),
      )
      .subscribe((value) => {
        this.filteredOptions$$.next(value);
      });

    if (this.selection === 'multiple') {
      this.selectedValues = [];
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length) {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.filteredOptions$$.next(this.options);
    }
  }

  private _filter(value: string): SelectInputOption[] {
    const filterValue = value.toLowerCase();

    return this.options.filter((option: SelectInputOption) => option.label.toLowerCase().includes(filterValue));
  }

  getOptionText(option: SelectInputOption): string {
    return option ? option.label : '';
  }

  setValue(value: SelectInputOption): void {
    if (this.selection === 'single') {
      this.selectedValue = value;
      this.selectedOptions.emit(this.selectedValue);
      this.formControl.setValue(this.selectedValue, { emitEvent: false });
    } else if (this.selection === 'multiple') {
      this.selectedValues.push(value);
      this.selectedOptions.emit(this.selectedValues);
      this.formControl.setValue(null, { emitEvent: false });
    }
  }

  handleOnFocus(): void {
    this.isActive = true;
  }

  handleOnBlur(): void {
    this.isActive = false;
    const selectValue = this.formControl.value;
    if (selectValue) {
      if (typeof selectValue === 'string') {
        this.handleOnInputBlur(selectValue);
      }
    }
  }

  handleOnInputBlur(selectValue: string): void {
    this.subscriptions.push(
      this.filteredOptions$$
        .asObservable()
        .pipe(
          tap((options: SelectInputOption[]) => {
            this.checkForValidInput(selectValue, options);
          }),
          first(),
        )
        .subscribe(() => {
          this.unsubscribeToInputBlur();
        }),
    );
  }

  handleOnSelectionChange(event: MatOptionSelectionChange): void {
    // there is bug where two events fire when you select a new option and a previous one was selected
    // the one we care about is the one where `isUserInput` is true
    // https://github.com/angular/components/issues/4094
    if (event.isUserInput) {
      const selectedValue = event.source.value;
      this.setValue(selectedValue);
    }
  }

  checkForValidInput(selectValue: string, options: SelectInputOption[]): void {
    if (options && options.length) {
      if (options.length === 1) {
        const option = options[0];
        if (option.label.toLowerCase() === selectValue.toLowerCase()) {
          this.setValue(option);
          return;
        }
      }
    }

    this.resetInput();
  }

  resetInput(): void {
    const resetValue = this.selection === 'single' ? this.selectedValue : null;
    this.formControl.setValue(resetValue, { emitEvent: false });
  }

  unsubscribeToInputBlur(): void {
    if (this.subscriptions.length) {
      this.subscriptions.shift()?.unsubscribe();
    }
  }

  getOption(obj: SelectInputOption): SelectInputOptionInterface | null {
    if ('options' in obj) {
      return null;
    }
    return obj as SelectInputOptionInterface;
  }
}
